// CTW-DURMAH-STUDENT-MANUAL-01: Getting Started sections.
// Redaction boundary per docs/STUDENT_MANUAL_REDACTION_PROPOSAL.md.

import type { ManualSection } from "../types";

export const gettingStartedSections: ManualSection[] = [
  {
    slug: "what-durmah-is",
    title: "What Durmah is, and what it is not",
    summary:
      "Durmah is a structured study workspace for UK law students. It turns lectures, briefs, drafts and deadlines into one connected workflow — and it will not write your assessed work for you.",
    category: "getting-started",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Start here", "Overview"],
    keywords: [
      "what is durmah",
      "what does durmah do",
      "is durmah an essay writer",
      "durmah overview",
      "introduction",
      "explain durmah",
      "new to durmah",
    ],
    blocks: [
      {
        kind: "para",
        text: "Durmah takes the raw material of a law degree — lectures, reading, assignment briefs, past papers, your own drafts, your revision timetable — and turns it into one organised workflow. Everything connects: a lecture you upload feeds your vocabulary, your module coverage and, later, the feedback on your draft for that module.",
      },
      { kind: "heading", id: "what-it-is", text: "What Durmah is" },
      {
        kind: "bullets",
        items: [
          "A workspace that holds your lectures, modules, assignments, deadlines and legal vocabulary in one place, connected to each other.",
          "A study tutor that interrogates your reasoning, names relevant authorities, tests your structure, and tells you where your argument is thin.",
          "A diagnostic reviewer that reads your own draft and returns structured feedback you can act on before you submit.",
          "A planning system that shows your academic year, your deadlines and your coverage gaps in context.",
        ],
      },
      { kind: "heading", id: "what-it-is-not", text: "What Durmah is not" },
      {
        kind: "bullets",
        items: [
          "A ghostwriter. It will not draft, rewrite or supply prose for work you intend to submit.",
          "A grade predictor. Feedback is diagnostic. It carries no university mark and no predicted classification.",
          "A legal adviser. Everything Durmah produces is study support for legal education, not legal advice.",
          "A source of invented law. When Durmah lacks verified support for an answer, it says so and stops.",
        ],
      },
      {
        kind: "para",
        text: "That last point is the design decision that most distinguishes Durmah from a general-purpose chatbot. Legal answers are constrained to a verified layer of UK authorities and statutes. If a question falls outside it, you get an honest boundary instead of a confident fabrication:",
      },
      {
        kind: "quote",
        attribution: "What Durmah shows you",
        text: "Source note: Durmah does not have enough verified UK-law support to answer this fully. Verified answer boundary — I will not invent cases, statutes, citations, ratios, or principles. Upload the relevant lecture notes, reading list, or assignment brief for a grounded answer.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Fluent is not the same as grounded",
        text: "Any AI can produce a confident paragraph about a case that does not exist. Durmah is built so that it would rather tell you it does not know. Every substantive legal answer carries a source note telling you what it is based on and how much weight it can bear — read them.",
      },
    ],
    related: ["three-pillars", "academic-integrity-boundary", "your-first-week"],
  },

  {
    slug: "three-pillars",
    title: "Learn Law · Write Law · Speak Law",
    summary:
      "Every tool in Durmah sits under one of three competencies a law degree actually assesses. Knowing which pillar you are working in tells you which tool to reach for.",
    category: "getting-started",
    audience: "public",
    order: 2,
    readTime: "3 min read",
    tags: ["Overview", "Concepts"],
    keywords: [
      "three pillars",
      "learn write speak",
      "which tool should i use",
      "how is durmah organised",
      "structure of the app",
    ],
    blocks: [
      {
        kind: "para",
        text: "Support is always attached to a competency you are building, rather than a task you are avoiding. That is the organising idea behind the three pillars.",
      },
      {
        kind: "table",
        headers: ["Pillar", "What it covers", "Where you use it"],
        rows: [
          [
            "Learn Law",
            "Understanding doctrine, cases, statutes and legal vocabulary. Turning lectures and reading into durable knowledge.",
            "Lecture Intelligence · the Durmah Lexicon™ · My Modules · Research Hub · Exam Prep",
          ],
          [
            "Write Law",
            "Structuring an argument, applying law to facts, citing correctly, improving your own draft.",
            "The assignment workflow · Mark My Paper · IRAC and OSCOLA guidance",
          ],
          [
            "Speak Law",
            "Articulating reasoning out loud — seminars, moots, vivas, interviews.",
            "Durmah Voice · Quiz Me · Practice Desk",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The pillar most students skip",
        text: "Speak Law. Writing an argument and being able to defend it out loud are different skills, and seminars, moots and training-contract interviews test the second one. If you can explain a case fluently in writing but stumble saying it aloud, that gap will show up at exactly the wrong moment.",
      },
    ],
    related: ["what-durmah-is", "using-durmah-voice", "quiz-me"],
  },

  {
    slug: "plans-and-trial",
    title: "Plans, the free trial, and what is included",
    summary:
      "How access works: a full-access trial that needs no card, then monthly or annual membership. Includes what happens to your work if you cancel.",
    category: "getting-started",
    audience: "public",
    order: 3,
    readTime: "3 min read",
    tags: ["Access", "Billing"],
    keywords: [
      "price",
      "pricing",
      "how much does durmah cost",
      "free trial",
      "do i need a credit card",
      "subscription",
      "annual plan",
      "monthly plan",
      "lnat pack",
      "student discount",
      "cancel",
    ],
    blocks: [
      {
        kind: "para",
        text: "Durmah runs on a straightforward membership. There are no hidden tiers, no feature drip-feed, and no pressure tactics — the pricing philosophy page sets out those commitments in full.",
      },
      { kind: "heading", id: "the-trial", text: "The trial" },
      {
        kind: "bullets",
        items: [
          "Full access to the workspace — not a cut-down demo.",
          "No credit card is required, and no payment method is collected.",
          "You are never charged automatically, so there is nothing to remember to cancel.",
          "Founding-cohort and ambassador students receive a longer window.",
        ],
      },
      {
        kind: "callout",
        tone: "note",
        title: "Current prices",
        text: "Live prices for the monthly plan, the annual plan and the LNAT pack are always shown on the pricing page. We deliberately do not repeat the figures here so that this manual can never drift out of date on something as important as what you pay.",
      },
      { kind: "heading", id: "paying", text: "Paying, and having someone else pay" },
      {
        kind: "bullets",
        items: [
          "Card payment is handled by Stripe. Durmah never sees or stores your card details.",
          "Promotional codes are accepted at checkout.",
          "If a parent or guardian is paying, you can generate a secure payment link from your billing page and send it to them. They do not need a Durmah account.",
          "Cancelling takes effect at the end of your current period — you keep access until then.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "We do not sell better grades",
        text: "Durmah is priced as a study companion, not as an outcome. Nothing on the pricing page promises a mark, a classification, or an improvement in your results, because no honest product could.",
      },
    ],
    related: ["billing-and-account", "what-durmah-is", "getting-help"],
  },

  {
    slug: "getting-access",
    title: "Getting access to Durmah",
    summary:
      "Start shared UK law study with Google sign-in. Adults can join as UK law students or independent learners; a university email is not required for private study.",
    category: "getting-started",
    audience: "public",
    order: 4,
    readTime: "2 min read",
    tags: ["Access", "Start here"],
    keywords: [
      "sign up",
      "how do i join",
      "get access",
      "founding trial",
      "waiting list",
      "invitation",
      "is my university supported",
      "which universities",
    ],
    blocks: [
      {
        kind: "steps",
        intro: "Start with the shared signup route.",
        items: [
          "Open Start your free trial from the public site. Choose whether you are a UK law student or an independent learner, and confirm that you are 18 or older. UK law students select their actual university.",
          "Continue with Google, then complete your private profile with your actual name and learner status. UK law students also provide their law course and study stage. Read and confirm the accuracy declaration.",
          "Begin with an original study question. No invitation, compulsory document upload or university-email pairing is required for private study. Current trial availability, allowances and paid terms are shown on the signup and pricing pages.",
        ],
      },
      {
        kind: "para",
        text: "Durmah provides a shared UK law-study workspace. Your profile and the permitted material you choose to add supply your personal study context. Selecting a university is a self-declaration, not verified affiliation, institutional endorsement or confirmation that Durmah covers its current syllabus. University-specific pages do not establish that a curriculum pack is current or available.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "Independent from every university",
        text: "Durmah is not affiliated with, endorsed by, or operated on behalf of any university. University names describe the academic context you provide. Follow your current programme, module and assessment instructions; product descriptions cannot grant permission to use AI.",
      },
    ],
    related: ["signing-in", "your-first-week", "plans-and-trial"],
  },

  {
    slug: "signing-in",
    title: "Signing in to your private study workspace",
    summary:
      "Use Google to return to your account. University-email verification is separate from private study and is only for Premier Lounge admission.",
    category: "getting-started",
    audience: "public",
    order: 5,
    readTime: "3 min read",
    tags: ["Access", "Start here"],
    keywords: [
      "login",
      "log in",
      "sign in",
      "cant sign in",
      "google sign in",
      "university email",
      "verification code",
      "verify my email",
      "pairing",
      "wrong university",
      "password",
    ],
    blocks: [
      {
        kind: "steps",
        intro: "Signing in for the first time:",
        items: [
          "Use Start your free trial if you are new, or Sign in if you already have an account. Confirm that you are 18 or older.",
          "For signup, choose your actual learner status. UK law students select their university; independent learners do not claim a student affiliation.",
          "Select Continue with Google and use the Google account you intend to keep using for Durmah.",
          "After signing in, complete any required private profile details accurately. Returning members use their existing account and access; signing in does not restart a consumed trial.",
          "Begin in your private workspace. Add study context or permitted materials when useful; university-email pairing is not required for this step.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Private study and Premier Lounge access are separate",
        text: "Your private learner profile records the context you declare. It does not verify university affiliation or grant community access. University-email verification is only for the separate Premier Lounge admission process; follow that area's current availability and admission requirements.",
      },
      { kind: "heading", id: "if-it-goes-wrong", text: "If something goes wrong" },
      {
        kind: "bullets",
        items: [
          "If Google sign-in does not complete, read the message on the sign-in page and retry with the same Google account. Contact support if access remains unavailable.",
          "If your university is not in the signup list, use the contact link to ask for it to be added. Do not choose a different institution to proceed.",
          "If your private profile is incomplete or inaccurate, complete or correct it before relying on personalised study context. Contact support if you cannot correct it.",
          "If you are seeking Premier Lounge admission, follow its separate instructions. A Lounge verification problem does not change the private-study signup requirements.",
        ],
      },
      {
        kind: "callout",
        tone: "note",
        title: "No student password",
        text: "Ordinary members sign in with Google, so there is no separate Durmah password for this route. If you lose access to your Google account, contact support rather than creating a second Durmah account.",
      },
    ],
    related: ["your-first-week", "getting-access", "getting-help"],
  },

  {
    slug: "your-first-week",
    title: "Your first week: what to set up, in order",
    summary:
      "The shortest path from an empty workspace to one that is genuinely useful. Do these five things and everything else in Durmah starts working properly.",
    category: "getting-started",
    audience: "public",
    order: 6,
    readTime: "4 min read",
    tags: ["Start here", "Setup"],
    keywords: [
      "first week",
      "getting started",
      "setup",
      "set up",
      "onboarding",
      "what should i do first",
      "new student",
      "quick start",
      "how to begin",
    ],
    blocks: [
      {
        kind: "para",
        text: "Durmah gets sharper the more it knows about your actual degree. An empty workspace can still answer questions, but it cannot prioritise your week or ground feedback in your own materials. These five steps take about twenty minutes in total and are worth doing in this order.",
      },
      {
        kind: "steps",
        items: [
          "Set your year of study. Everything — module suggestions, resources, the framing of your academic year — adapts to it. You can change it later from your profile.",
          "Add your modules. This is the single highest-value setup step. Module codes, titles and teaching staff take a few minutes and unlock coverage tracking, exam prep and module-aware feedback.",
          "List your syllabus topics for each module. This turns 'I think I have missed something' into a specific list of what you have not covered yet.",
          "Upload one lecture. Do this early, with a lecture you have already attended, so you can see what Durmah produces from it before you rely on it under pressure.",
          "Import your calendar or timetable. Once your deadlines are in, your dashboard can actually tell you what matters today rather than guessing.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The guided walkthrough",
        text: "The first time you reach your workspace, Durmah offers an eight-chapter guided introduction covering syllabus, lectures, vocabulary, assignments, draft review and voice. It is optional, but it is the fastest way to understand how the pieces connect. You can leave and come back to it.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "You do not have to do it all at once",
        text: "Nothing is blocked if you skip a step. Add one module and one lecture and the workspace already works; fill in the rest as term goes on.",
      },
    ],
    related: ["dashboard-tour", "my-modules", "uploading-a-lecture"],
  },
];
